import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Trash2, Edit2, Save, X, KeyRound, UserCog } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fbAuth } from "@/lib/firebase";
import MfaSettings from "@/components/auth/MfaSettings";
import { PLAN_LABELS } from "@/lib/plans";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const ROLE_LABELS = {
  admin: "Admin",
  bid_manager: "Bid Manager",
  member: "Team Member",
};

const ROLE_PILL = {
  admin: "bg-brand-soft text-brand border-brand/30",
  bid_manager: "bg-warn-soft text-warn border-warn/30",
  member: "bg-secondary text-ink-muted border-ink-line",
};

export default function Admin() {
  const { user: me, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // user object being edited
  const [accountOpen, setAccountOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name || u.email} is now ${ROLE_LABELS[role]}`);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const changePlan = async (u, patch, msg) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      toast.success(msg);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const del = async (u) => {
    if (!confirm(`Remove ${u.email} from the workspace?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };

  const counts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand text-white grid place-items-center rounded-sm">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Administration</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Users & Permissions</h1>
            <p className="text-sm text-ink-muted mt-1.5">
              New signups join as <span className="font-medium text-ink">Team Member</span> by default. Assign a role to grant capabilities.
            </p>
          </div>
        </div>
        <button
          onClick={() => setAccountOpen(true)}
          data-testid="my-account-btn"
          className="flex items-center gap-2 px-4 py-2.5 border border-ink-line rounded-sm text-sm hover:bg-secondary transition-colors"
        >
          <UserCog className="w-4 h-4" /> My account
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { k: "admin", label: "Admins" },
          { k: "bid_manager", label: "Bid Managers" },
          { k: "member", label: "Team Members" },
        ].map((x) => (
          <div key={x.k} className="card-flat p-4" data-testid={`count-${x.k}`}>
            <div className="stat-label">{x.label}</div>
            <div className="font-num text-3xl font-bold mt-2">{counts[x.k] || 0}</div>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="card-flat overflow-hidden">
        <div className="px-5 py-3.5 border-b border-ink-line flex items-center justify-between">
          <div className="font-display font-semibold tracking-tight">All users</div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted font-num">{users.length} total</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-ink-line">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium">Plan</th>
              <th className="px-5 py-3 font-medium">Joined</th>
              <th className="px-5 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-ink-line last:border-b-0 hover:bg-secondary/60 transition-colors">
                <td className="px-5 py-3 font-medium">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-soft text-brand grid place-items-center font-semibold text-[11px] rounded-sm">
                      {(u.name || u.email || "U").slice(0, 2).toUpperCase()}
                    </div>
                    <span>{u.name || "—"}</span>
                    {u.id === me?.id && <span className="text-[10px] uppercase tracking-wider bg-brand-soft text-brand px-1.5 py-0.5 rounded-sm">You</span>}
                  </div>
                </td>
                <td className="px-5 py-3 text-ink-muted font-num text-xs">{u.email}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`chip ${ROLE_PILL[u.role] || ""}`} data-testid={`role-pill-${u.id}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {me?.role === "admin" && u.id !== me?.id && (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        data-testid={`role-select-${u.id}`}
                        className="px-2 py-1 border border-ink-line bg-white rounded-sm text-xs"
                      >
                        <option value="admin">Admin</option>
                        <option value="bid_manager">Bid Manager</option>
                        <option value="member">Team Member</option>
                      </select>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <PlanCell u={u} onChange={changePlan} />
                </td>
                <td className="px-5 py-3 text-ink-muted text-xs font-num">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-5 py-3 text-right">
                  {me?.role === "admin" && (
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(u)}
                        data-testid={`edit-user-${u.id}`}
                        className="p-1.5 text-ink-muted hover:text-brand hover:bg-secondary rounded-sm transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {u.id !== me?.id && (
                        <button
                          onClick={() => del(u)}
                          data-testid={`delete-user-${u.id}`}
                          className="p-1.5 text-ink-muted hover:text-warn hover:bg-warn-soft rounded-sm transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {accountOpen && (
        <AccountModal
          me={me}
          onClose={() => setAccountOpen(false)}
          onSaved={(u) => { setUser(u); setAccountOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [f, setF] = useState({ name: user.name || "", email: user.email || "", role: user.role });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (f.name !== user.name) payload.name = f.name;
      if (f.email !== user.email) payload.email = f.email;
      if (f.role !== user.role) payload.role = f.role;
      if (!Object.keys(payload).length) { onClose(); return; }
      await api.patch(`/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const inputCls = "w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">Edit user</div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="stat-label block mb-1.5">Full name</label>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="edit-user-name" />
          </div>
          <div>
            <label className="stat-label block mb-1.5">Email</label>
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="edit-user-email" />
          </div>
          <div>
            <label className="stat-label block mb-1.5">Role</label>
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={inputCls} data-testid="edit-user-role">
              <option value="admin">Admin</option>
              <option value="bid_manager">Bid Manager</option>
              <option value="member">Team Member</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary">Cancel</button>
            <button onClick={save} disabled={loading} data-testid="edit-user-save" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5">
              <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCell({ u, onChange }) {
  const info = u.plan_info || {};
  const plan = u.plan || "explorer";
  const exp = u.plan_expires_at ? u.plan_expires_at.slice(0, 10) : "";
  if (u.role === "admin") {
    return <span className="chip bg-brand-soft text-brand border-brand/30" data-testid={`plan-admin-${u.id}`}>Full access (admin)</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      <select
        value={plan}
        onChange={(e) => onChange(u, { plan: e.target.value }, `${u.name || u.email} → ${PLAN_LABELS[e.target.value]}`)}
        data-testid={`plan-select-${u.id}`}
        className="px-2 py-1 border border-ink-line bg-white rounded-sm text-xs"
      >
        {Object.entries(PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {(plan === "explorer" || plan === "growth" || plan === "professional") && (
        <div className="flex items-center gap-1 text-[10px] text-ink-muted">
          until
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid={`plan-expiry-${u.id}`} className="px-1.5 py-0.5 border border-ink-line rounded-sm text-[11px] font-num hover:bg-secondary">
                {exp ? format(new Date(`${exp}T12:00:00`), "dd MMM yyyy") : "set date"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={exp ? new Date(`${exp}T12:00:00`) : undefined}
                defaultMonth={exp ? new Date(`${exp}T12:00:00`) : undefined}
                onSelect={(d) => d && onChange(u, { plan_expires_at: `${format(d, "yyyy-MM-dd")}T23:59:59+00:00` }, "Plan end date updated")}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {info.expired && <span className="text-warn font-medium" data-testid={`plan-expired-${u.id}`}>expired</span>}
          {info.trial && !info.expired && <span className="text-brand font-medium" data-testid={`plan-trial-${u.id}`}>trial</span>}
          {!exp && <span>(no end date)</span>}
        </div>
      )}
    </div>
  );
}

export function AccountModal({ me, onClose, onSaved }) {
  const { changePassword } = useAuth();
  const [tab, setTab] = useState("profile");
  const [f, setF] = useState({ name: me?.name || "", email: me?.email || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const fu = fbAuth.currentUser;
  const hasPassword = !fu || fu.providerData.some((p) => p.providerId === "password");

  const inputCls = "w-full px-3 py-2 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand";

  const saveProfile = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (f.name !== me.name) payload.name = f.name;
      if (f.email !== me.email) payload.email = f.email;
      if (!Object.keys(payload).length) { onClose(); return; }
      const { data } = await api.patch(`/auth/me`, payload);
      toast.success("Profile updated");
      onSaved(data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const changePw = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 chars"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    const r = await changePassword(pw.current_password, pw.new_password);
    setLoading(false);
    if (!r.ok) { toast.error(r.error); return; }
    toast.success("Password updated");
    setPw({ current_password: "", new_password: "", confirm: "" });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()} data-testid="account-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="font-display font-bold tracking-tight">My account</div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex border-b border-ink-line">
          <button
            onClick={() => setTab("profile")}
            data-testid="account-tab-profile"
            className={`px-5 py-3 text-[13px] font-medium border-b-2 ${tab === "profile" ? "border-brand text-brand" : "border-transparent text-ink-muted"}`}
          >
            Profile
          </button>
          <button
            onClick={() => setTab("password")}
            data-testid="account-tab-password"
            className={`px-5 py-3 text-[13px] font-medium border-b-2 ${tab === "password" ? "border-brand text-brand" : "border-transparent text-ink-muted"}`}
          >
            Password
          </button>
          <button
            onClick={() => setTab("mfa")}
            data-testid="account-tab-mfa"
            className={`px-5 py-3 text-[13px] font-medium border-b-2 ${tab === "mfa" ? "border-brand text-brand" : "border-transparent text-ink-muted"}`}
          >
            2-step
          </button>
        </div>

        {tab === "mfa" && <MfaSettings />}

        {tab === "password" && !hasPassword && (
          <p className="p-6 text-sm text-ink-muted" data-testid="pw-google-note">You sign in with Google, so your password is managed in your Google account.</p>
        )}

        {tab === "profile" && (
          <div className="p-6 space-y-4">
            <div>
              <label className="stat-label block mb-1.5">Full name</label>
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="account-name-input" />
            </div>
            <div>
              <label className="stat-label block mb-1.5">Email</label>
              <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="account-email-input" />
              <div className="text-[11px] text-ink-muted mt-1.5">Changing your email also changes the address you sign in with.</div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary">Cancel</button>
              <button onClick={saveProfile} disabled={loading} data-testid="account-save-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5">
                <Save className="w-4 h-4" /> {loading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}

        {tab === "password" && hasPassword && (
          <div className="p-6 space-y-4">
            <div>
              <label className="stat-label block mb-1.5">Current password</label>
              <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="pw-current" />
            </div>
            <div>
              <label className="stat-label block mb-1.5">New password</label>
              <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="pw-new" />
            </div>
            <div>
              <label className="stat-label block mb-1.5">Confirm new password</label>
              <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="pw-confirm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary">Cancel</button>
              <button onClick={changePw} disabled={loading} data-testid="pw-save-btn" className="px-5 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm flex items-center gap-1.5">
                <KeyRound className="w-4 h-4" /> {loading ? "Updating…" : "Update password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
